<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import type { PaginationArgs } from 'pejer/src/types/types'
import { MaybeRef, unref } from 'vue'
import { cn } from '../lib/utils'
import Button from './ui/button/Button.vue'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from './ui/pagination'

const props = defineProps<{
  totalSize: MaybeRef<number>
  currentSize: MaybeRef<number>
  pagination: MaybeRef<PaginationArgs>
  isFetching: MaybeRef<boolean>
  class?: string
}>()

const emit = defineEmits<{
  (e: 'update-pagination', page: number): void
  (e: 'increment-page'): void
  (e: 'decrement-page'): void
}>()

const updatePagination = (page: number) => {
  emit('update-pagination', page)
}

const incrementPage = () => {
  emit('increment-page')
}

const decrementPage = () => {
  emit('decrement-page')
}
</script>
<template>
  <Pagination
    :class="cn('justify-center flex', unref(props.class))"
    v-slot="{ page }"
    :total="unref(totalSize) != -1 ? unref(totalSize) : unref(currentSize)"
    :sibling-count="1"
    show-edges
    :items-per-page="unref(pagination).size"
    :page="unref(pagination).page"
  >
    <PaginationList v-slot="{ items }" class="flex items-center gap-1">
      <PaginationFirst @click="updatePagination(1)" />
      <PaginationPrev @click="decrementPage" />
      <template v-for="(item, index) in items" :key="index">
        <PaginationListItem v-if="item.type === 'page'" :value="item.value" as-child>
          <Button
            :variant="item.value === page ? 'default' : 'outline'"
            class="p-0"
            :class="{
              'w-10 h-10': item.value === page,
              'w-9 h-9': item.value !== page,
            }"
            @click="updatePagination(item.value)"
          >
            {{ item.value }}
          </Button>
        </PaginationListItem>
        <PaginationEllipsis v-else :key="item.type" :index="index" />
      </template>

      <div v-if="isFetching" class="w-10 h-10 flex justify-center items-center">
        <Loader2 class="animate-spin w-4 h-4" />
      </div>
      <PaginationEllipsis v-else-if="unref(totalSize) == -1" />
      <PaginationNext @click="incrementPage" />
      <PaginationLast
        @click="
          updatePagination(
            Math.ceil(
              (unref(totalSize) != -1 ? unref(totalSize) : unref(currentSize)) /
                unref(pagination).size
            )
          )
        "
      />
    </PaginationList>
  </Pagination>
</template>
